import React, { useState, useEffect } from 'react';
import { couponsAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Copy, Trash2, Plus, Loader2, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state
  const [couponCode, setCouponCode] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [expiryDate, setExpiryDate] = useState(new Date());
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await couponsAPI.getAll();
        setCoupons(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, []);

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const handleDelete = async (id) => {
    try {
      await couponsAPI.delete(id);
      setCoupons(coupons.filter(c => c.id !== id));
    } catch (error) {
    }
  };

  const handleCreateCoupon = async () => {
    try {
      const discount = discountType === 'percentage' 
        ? `${discountValue}%` 
        : `$${discountValue}`;
      
      const newCoupon = {
        code: couponCode.toUpperCase(),
        discount,
        client: clientName || '-',
        usage: `0/${usageLimit || '100'}`,
        expiry: format(expiryDate, 'MMM d, yyyy'),
        status: 'Active'
      };
      const response = await couponsAPI.create(newCoupon);
      setCoupons([response.data, ...coupons]);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
    }
  };

  const resetForm = () => {
    setCouponCode('');
    setDiscountType('percentage');
    setDiscountValue('');
    setUsageLimit('');
    setExpiryDate(new Date());
    setClientName('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Coupons</h1>
          <p className="text-gray-500">Manage discount codes synced to WooCommerce</p>
        </div>
        <Button 
          onClick={() => setIsModalOpen(true)}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10"
        >
          <Plus size={16} className="mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Coupons Table */}
      <Card className="bg-white border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{coupons.length} Coupons</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Expiry</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{coupon.code}</span>
                      <button
                        onClick={() => handleCopyCode(coupon.code)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Copy code"
                      >
                        <Copy className="text-gray-400" size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{coupon.discount}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{coupon.client}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{coupon.usage}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{coupon.expiry}</td>
                  <td className="px-4 py-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      coupon.status === 'Active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {coupon.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button 
                      onClick={() => handleDelete(coupon.id)}
                      className="p-1 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="text-gray-400 hover:text-red-500" size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {coupons.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No coupons found. Create your first coupon to get started.</p>
          </div>
        )}
      </Card>

      {/* Create Coupon Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Coupon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Coupon Code *</Label>
              <Input
                placeholder="SUMMER2025"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  placeholder={discountType === 'percentage' ? '10' : '50'}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Usage Limit</Label>
              <Input
                type="number"
                placeholder="100"
                value={usageLimit}
                onChange={(e) => setUsageLimit(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiryDate ? format(expiryDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={setExpiryDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Assign to Client (Optional)</Label>
              <Input
                placeholder="Client name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </div>

            <Button
              onClick={handleCreateCoupon}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!couponCode || !discountValue}
            >
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponsPage;
